// ── hostScopePin.js — "this tab is on a customer's own domain" ────────────
//
// WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1, the THIRD defect: the switchOrg
// race. Measured on work.astio.ai as 4 wrong of 8 loads before any fix, and
// still 1 wrong of 5 after the structural fix shipped as 78f9fe5bb.
//
// THE RACE. On a white-label host with the MWT flag off, TWO un-awaited
// writers state an org into the same SDK context on the same tick:
//   · customDomainBoot's pinned path — sdk.switchOrg(<the HOST's org>,
//     { skipPersist: true }), which is correct for this tab; and
//   · auth.js's drift-correction re-mint — sdk.switchOrg(<the MONGO/claim
//     active org>), which is the org my.symbols last used and is correct
//     everywhere EXCEPT here.
// Neither awaits the other, so whichever settles last wins, and the customer
// sees another tenant's workspace under their own domain about a third of the
// time. That is the exact class WHITELABEL-1 exists to prevent.
//
// WHY A STAND-DOWN AND NOT AN ORDERING. Three options were on the table.
// Sequencing the boot after auth, and re-asserting the pin after the re-mint,
// BOTH still let auth write the wrong org first — so the wrong tenant is
// briefly VISIBLE, and on a customer's branded domain a visible wrong tenant
// IS the defect, not a transient. Only standing auth down never writes the
// wrong value at all. relman made this argument and it is the deciding one.
//
// WHY ITS OWN MODULE. auth.js must read this pin, and customDomainBoot.js
// must write it. Importing customDomainBoot from auth would couple the auth
// tick to the whole boot ladder (and to `_wsShared`) for one string. Same
// reason whitelabelBrand.js sits beside customDomainBoot rather than inside
// it. A leaf module with no imports also stays trivially testable.

// Module-level, not on `window`: this is per-DOCUMENT state and must not be
// reachable by, or shared with, anything outside the bundle. It is also NOT
// persisted — the pin is re-derived from the hostname on every load, so a
// stale pin cannot outlive the page that earned it.
let _hostScopeOrgId = null

/**
 * State the org this HOST is pinned to. Called by customDomainBoot the moment
 * the scope lookup lands — deliberately BEFORE the orgs ladder finishes, so
 * the window in which auth can still resolve the ambient org is as small as
 * the network allows rather than as long as the ladder runs.
 *
 * Passing a falsy id CLEARS the pin, which is what a failed or empty lookup
 * must do: fail closed, exactly like the resolver it sits beside.
 */
export const setHostScopeOrgId = (orgId) => {
  _hostScopeOrgId = orgId ? String(orgId) : null
}

/**
 * The pinned org id, or null on a platform host / before the lookup lands /
 * after a failed lookup. Null is the "no opinion" answer and every caller must
 * treat it as such — an absent pin never overrides anything.
 *
 * NOTE FOR CALLERS: this function does NOT check membership. The one caller
 * (resolveActiveOrgId) validates the id against the user's org list before
 * honouring it, so a host naming an org the signed-in user does not belong to
 * falls through to the normal ladder instead of ghost-activating. Any new
 * caller MUST do the same.
 */
export const getHostScopeOrgId = () => _hostScopeOrgId

/** Test-only reset. Module state would otherwise leak between cases. */
export const __resetHostScopePin = () => {
  _hostScopeOrgId = null
}
